import { IPaymentMethodReq } from '@monetize/types/app';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { doCreatePaymentMethodByAccount } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import { useGetPaymentGatewayById } from '../../../api/paymentsService';
import AppLoading from '../../../components/App/AppLoading';
import { getAccountPaymentMethodRoute } from '../../../constants/routes';
import { useQuery } from '../../../hooks';
import { useToast } from '../../../services/toast';
import { tempPaymentMethodState } from '../../../store/global.store';
import { processClientSecretId } from '../../../utils/stripe';

export const PaymentMethodsStripeCallback = () => {
  const query = useQuery();
  const { accountId = '', paymentGatewayId = '' }: any = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  // load saved payment method form in local state
  const [tempPaymentMethod, setTempPaymentMethod] = useAtom(
    tempPaymentMethodState,
  );
  const clientSecretId = query.get('setup_intent_client_secret');

  const { data: gateway, isLoading } = useGetPaymentGatewayById({
    id: paymentGatewayId,
  });

  useEffect(() => {
    if (!accountId || !gateway) {
      return;
    }
    (async () => {
      if (!gateway) {
        handleApiErrorToast('Sorry, failed to load payment gateway.');
        return;
      }
      const stripe = await loadStripe(gateway.publicKey);

      if (!stripe) {
        handleApiErrorToast('Sorry, failed to load stripe.');
        return;
      }
      if (!clientSecretId) {
        handleApiErrorToast('Sorry, failed to connect.');
        return;
      }
      if (!tempPaymentMethod) {
        handleApiErrorToast('Sorry, payment method information is incorrect.');
        return;
      }

      const { setupIntent, message } = await processClientSecretId(
        stripe,
        clientSecretId,
      );

      const newPaymentMethod: IPaymentMethodReq = {
        billingDetails: {
          fullName: tempPaymentMethod.contact?.fullName || '',
          email: tempPaymentMethod.contact?.email,
          phone: tempPaymentMethod.contact?.phone,
          title: tempPaymentMethod.contact?.title,
          line1: tempPaymentMethod.contact?.address?.line1,
          line2: tempPaymentMethod.contact?.address?.line2,
          city: tempPaymentMethod.contact?.address?.city,
          state: tempPaymentMethod.contact?.address?.state,
          postalCode: tempPaymentMethod.contact?.address?.postalCode,
          country: tempPaymentMethod.contact?.address?.country,
        },
        paymentMethodName: tempPaymentMethod.paymentMethodName,
        paymentToken: setupIntent.id,
        paymentGatewayId: gateway.id,
      };

      await doCreatePaymentMethodByAccount(accountId, newPaymentMethod);

      // make sure to clear
      setTempPaymentMethod(null);

      addToast({
        summary: 'Stripe Connected.',
        detail: message,
        severity: 'info',
      });
      navigate(getAccountPaymentMethodRoute(accountId), {});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, clientSecretId, gateway, setTempPaymentMethod]);

  if (isLoading) {
    return <AppLoading loadingText="Validating connection..." />;
  }

  return null;
};
